import { useState, useCallback, useRef, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import { Button, Container, ProgressBar, Card, Spinner } from "react-bootstrap";
import { Upload, X } from "lucide-react";

const VideoUpload = ({
  onUpload,
  url, // Existing video URL from the database
  maxDuration = 30,
  maxSize = 100 * 1024 * 1024, // 100MB
  videoUploadProgress,
}) => {
  const [videoFile, setVideoFile] = useState(null);
  const [videoPreview, setVideoPreview] = useState(url || ""); // Initialize with `url` if provided
  const [isRecording, setIsRecording] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState("");

  const videoRef = useRef(null);

  useEffect(() => {
    setVideoPreview(url);
  }, [url]);
  useEffect(() => {
    // Cleanup old video preview URLs to prevent memory leaks
    return () => {
      if (videoPreview && videoPreview.startsWith("blob:")) {
        URL.revokeObjectURL(videoPreview);
      }
    };
  }, [videoPreview]);

  const onDrop = useCallback(
    (acceptedFiles) => {
      const file = acceptedFiles[0];
      if (!file) return;

      // Validate file size
      if (file.size > maxSize) {
        setError(`File size must be less than ${maxSize / 1024 / 1024}MB`);
        return;
      }

      // Validate video duration
      const video = document.createElement("video");
      video.preload = "metadata";
      const newVideoPreview = URL.createObjectURL(file);

      video.src = newVideoPreview;

      video.onloadedmetadata = () => {
        if (video.duration > maxDuration) {
          setError(`Video duration must be less than ${maxDuration} seconds`);
          URL.revokeObjectURL(newVideoPreview); // Revoke invalid blob URL
          return;
        }

        if (videoPreview && videoPreview.startsWith("blob:")) {
          URL.revokeObjectURL(videoPreview);
        }

        setVideoFile(file);
        setVideoPreview(newVideoPreview);
        setError("");
      };

      video.onerror = () => {
        setError("Error loading video. Please try again.");
        URL.revokeObjectURL(newVideoPreview);
      };
    },
    [maxDuration, maxSize, videoPreview],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "video/*": [] },
    maxFiles: 1,
  });
  const handleUpload = async () => {
    if (!videoFile) {
      setError("No video file selected for upload.");
      return;
    }

    try {
      setUploadProgress(0);
      setIsUploading(true);
      await onUpload(videoFile);
      setVideoFile(null);
      setIsUploading(false);
      setUploadProgress(100);
    } catch (err) {
      setError("Failed to upload video");
      setUploadProgress(0);
      setIsUploading(false);
    }
  };
  return (
    <div className="mb-40">
      <Container className="max-w-2xl mx-auto p-0">
        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer
          ${
            isDragActive
              ? "border-primary bg-primary/10"
              : "border-muted-foreground"
          }`}
        >
          {!videoPreview ? (
            <Card className="video-player-main">
              <Card.Body className="d-flex justify-content-center flex-column">
                <input {...getInputProps()} />
                <Upload className="w-12 h-12 mx-auto mb-2 text-muted-foreground" />
                <p className="text-lg font-medium video-player-main__label">
                  Upload a video here ({maxDuration} sec max)
                </p>
                <p className="text-sm text-muted-foreground mt-2 video-player-main__des">
                  Tell us what you're looking for
                </p>
                <div className="mt-2 flex gap-2 justify-center">
                  <Button
                    variant="primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      const input = document.createElement("input");
                      input.type = "file";
                      input.accept = "video/*";
                      input.onchange = (e) => {
                        const file = e.target.files?.[0];
                        if (file) onDrop([file]);
                      };
                      input.click();
                    }}
                    disabled={isUploading}
                  >
                    Upload Video
                  </Button>
                </div>
              </Card.Body>
            </Card>
          ) : (
            <Card className="relative">
              <Card.Body className="p-0 m-2" style={{ position: "relative" }}>
                {isUploading && (
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: "rgba(255, 255, 255, 0.8)", // Semi-transparent overlay
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      zIndex: 10,
                    }}
                  >
                    <Spinner animation="border" size="sm" className="me-2" />
                    {videoUploadProgress > 0 && (
                      <span>{videoUploadProgress}% Video Uploaded</span>
                    )}
                  </div>
                )}
                <video
                  ref={videoRef}
                  src={videoPreview || url}
                  className="w-full rounded-lg"
                  controls
                  style={{
                    width: "90%",
                    height: "auto",
                    maxHeight: "300px",
                    objectFit: "contain",
                  }}
                />

                <Button
                  className="top-2 right-2 p-0"
                  style={{ fontSize: "4px", position: "absolute" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setVideoFile(null);
                    setVideoPreview("");
                  }}
                >
                  <X />
                </Button>
              </Card.Body>
            </Card>
          )}
        </div>

        {error && <div className="mt-4 text-danger text-sm">{error}</div>}

        {videoFile && !error && (
          <div className="mt-4">
            <Button
              variant="primary"
              className="w-full mx-2"
              onClick={handleUpload}
              disabled={videoUploadProgress > 0 && videoUploadProgress < 100}
            >
              Looks good? Confirm
            </Button>
          </div>
        )}
      </Container>
    </div>
  );
};

export default VideoUpload;
